/**
 * Which form field a focus or blur landed on.
 *
 * Every focus-driven affordance on these screens keys off the field's
 * `name` — travel's cursor, the document following the caret, the keyboard
 * shortcuts, and answering a field by tabbing off it. Reading it straight from
 * the event target works for an `<input>` and silently fails for a `<Select>`,
 * because MUI focuses a `div[role="combobox"]` and puts the name on a hidden
 * native input beside it. The dropdowns were invisible to all of it.
 *
 * A select is not a rare case on these forms — physical state, unit of measure,
 * container type, every tri-state — so the lookup has to handle both rather than
 * each caller remembering to.
 */

/**
 * The field's own root, whichever piece of it took focus.
 *
 * `MuiInputBase-root` first because it is the tighter of the two: a
 * `FormControl` can hold more than one input, and starting from the outer one
 * would resolve a compound field to whichever input happened to be first.
 */
const FIELD_ROOT = ".MuiInputBase-root, .MuiFormControl-root";

export function fieldPathOf(target: EventTarget | null): string | null {
  if (!(target instanceof HTMLElement)) return null;

  const own = target.getAttribute("name");
  if (own != null && own.length > 0) return own;

  // A select: the name is on a sibling the reviewer can never focus.
  const named = target.closest<HTMLElement>(FIELD_ROOT)?.querySelector<HTMLElement>("[name]");
  const path = named?.getAttribute("name");
  return path != null && path.length > 0 ? path : null;
}
